import React, { useState, useRef, useEffect } from 'react';
import { Routes, Route, Navigate, Link } from 'react-router-dom';
import { AnalyticsManager } from './components/AnalyticsManager';
import { AuthReturnHandler } from './components/AuthReturnHandler';
import HomeRedirect from './components/HomeRedirect';
import './App.css';

import { useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import BillingPage from './pages/BillingPage';
import AdminPage from './pages/AdminPage';
import PrivacyPage from './pages/PrivacyPage';

const SIGN_IN_URL = process.env.REACT_APP_SIGN_IN_URL || '/sign-in';
const MARKETING_URL = process.env.REACT_APP_MARKETING_URL || '';

function UserMenu() {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const initials = user?.name
    ? user.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() ?? '?';

  return (
    <div className="user-menu" ref={menuRef}>
      <button
        className="user-menu-trigger"
        onClick={() => setOpen(o => !o)}
        aria-label="User menu"
        aria-expanded={open}
      >
        {user?.image ? (
          <img src={user.image} alt={user.name} className="user-menu-avatar" />
        ) : (
          <span className="user-menu-initials">{initials}</span>
        )}
      </button>
      {open && (
        <div className="user-menu-dropdown">
          <div className="user-menu-info">
            <span className="user-menu-name">{user?.name || 'User'}</span>
            <span className="user-menu-email">{user?.email}</span>
          </div>
          <hr className="user-menu-divider" />
          <a
            href={`${MARKETING_URL}/profile`}
            className="user-menu-item"
            onClick={() => setOpen(false)}
          >
            Profile
          </a>
          <a
            href={`${MARKETING_URL}/profile/orders`}
            className="user-menu-item"
            onClick={() => setOpen(false)}
          >
            Orders
          </a>
          <hr className="user-menu-divider" />
          <button
            className="user-menu-item"
            onClick={async () => { await signOut(); setOpen(false); }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function AppHeader() {
  const { isSignedIn } = useAuth();
  return (
    <header className="App-header">
      <div className="App-header-inner">
        <a href={MARKETING_URL || '/'} className="App-header-brand">
          <div className="App-header-logo-mark">
            <img src="/logo.png" alt="Bikendure logo" />
          </div>
          <span className="App-header-title">Bikendure</span>
        </a>

        <nav className="App-nav">
          <a href={MARKETING_URL || '/'} className="App-nav-link">AI Bike Fitting</a>
          <a href={`${MARKETING_URL}/#pricing`} className="App-nav-link">Pricing</a>
          <a href={`${MARKETING_URL}/about`} className="App-nav-link">About us</a>
          <a href={`${MARKETING_URL}/contact`} className="App-nav-link">Contact</a>
        </nav>

        <div className="App-nav-actions">
          {isSignedIn ? (
            <>
              <Link to="/onboarding" className="App-nav-app-btn">Open Bike Fit App</Link>
              <UserMenu />
            </>
          ) : (
            <a href={SIGN_IN_URL} className="App-nav-signin-btn">Log in</a>
          )}
        </div>
      </div>
    </header>
  );
}

function App() {
  return (
    <div className="App">
      <AppHeader />

      <main className="App-main">
        <Routes>
          <Route path="/sign-in/*" element={<SignInPage />} />
          <Route path="/sign-up/*" element={<SignUpPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />

          {/* Public routes — no auth required */}
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/video-upload" element={<DashboardPage />} />

          {/* Home is not a page — the landing page lives on the marketing site */}
          <Route path="/" element={<HomeRedirect />} />

          {/* Protected routes — require auth */}
          <Route element={<ProtectedRoute />}>
            <Route path="/billing" element={<BillingPage />} />
            <Route path="/admin" element={<AdminPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/onboarding" replace />} />
        </Routes>
      </main>

      <footer className="App-footer">
        <p>
          © 2026 Bikendure ·{' '}
          <a href={`${MARKETING_URL}/privacy`} style={{ color: 'inherit' }}>Privacy Policy</a>
          {' · '}
          <a href={`${MARKETING_URL}/terms`} style={{ color: 'inherit' }}>Terms of Service</a>
          {' · '}
          <button
            onClick={() => (window as any).silktideConsentManager?.getInstance()?.icon?.click()}
            style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, font: 'inherit' }}
          >
            Cookie Settings
          </button>
        </p>
      </footer>

      <AnalyticsManager />
      <AuthReturnHandler />
    </div>
  );
}

export default App;
